import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";

import SEO from "../data/seo";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const Container = styled.div<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 1.5rem ${props => props.theme.density.pageGutter} 3rem;

	@media (max-width: 768px) {
		padding: 1.25rem ${props => props.theme.density.pageGutterMobile} 2rem;
	}
`;

const Header = styled.div`
	margin-bottom: 2rem;
	animation: ${fadeInUp} 0.3s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.75rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const Subtitle = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	color: ${props => props.theme.colors.text.secondary};
	line-height: ${props => props.theme.typography.roles.body.lineHeight};

	@media (max-width: 768px) {
		font-size: 1.125rem;
	}
`;

const ServicesGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
	gap: 0.875rem;
	margin-bottom: 2rem;
	animation: ${fadeInUp} 0.3s ease-out 0.15s both;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
	}
`;

const ServiceCard = styled(Link)<{ theme: Theme }>`
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: ${props => props.theme.density.cardRadius};
	padding: ${props => props.theme.density.cardPadding};
	text-decoration: none;
	transition: border-color 0.2s ease, transform 0.2s ease;
	box-shadow: ${props => props.theme.shadows.base};

	&:hover {
		transform: translateY(-2px);
		border-color: ${props => props.theme.colors.primary.main};
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

const ServiceIcon = styled.div<{ theme: Theme }>`
	display: grid;
	place-items: center;
	width: 2.75rem;
	height: 2.75rem;
	margin-bottom: 1rem;
	color: ${props => props.theme.colors.primary.main};
	background: ${props => props.theme.colors.background.tertiary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 0.75rem;
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: 0.8rem;
	font-weight: 700;
`;

const ServiceTitle = styled.h2<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.title.fontSize};
	font-weight: ${props => props.theme.typography.roles.title.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.75rem;

	@media (max-width: 768px) {
		font-size: 1.125rem;
	}
`;

const ServiceSubtitle = styled.h3<{ theme: Theme }>`
	font-size: 1.125rem;
	font-weight: 500;
	color: ${props => props.theme.colors.primary.main};
	margin-bottom: 0.75rem;

	@media (max-width: 768px) {
		font-size: 1rem;
	}
`;

const ServiceDescription = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.bodySmall.fontSize};
	color: ${props => props.theme.colors.text.secondary};
	line-height: ${props => props.theme.typography.roles.bodySmall.lineHeight};
	margin-bottom: 1rem;
`;

const ServiceButton = styled.span<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 0.875rem;

	&::after {
		content: '→';
		transition: transform 0.2s ease;
	}

	${ServiceCard}:hover &::after {
		transform: translateX(4px);
	}
`;

const FooterWrapper = styled.div`
	margin-top: 3rem;
`;

const Services = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "services") || SEO[0];

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Services | ${userInfo.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="services" />
				<Container theme={theme}>
					<Header>
						<Title theme={theme}>{t.services.title}</Title>
						<Subtitle theme={theme}>{t.services.description}</Subtitle>
					</Header>

					<ServicesGrid>
						<ServiceCard to="/services/ad-spaces" theme={theme}>
							<ServiceIcon theme={theme}>AD</ServiceIcon>
							<ServiceTitle theme={theme}>{t.services.adSpaces.title}</ServiceTitle>
							<ServiceSubtitle theme={theme}>{t.services.adSpaces.subtitle}</ServiceSubtitle>
							<ServiceDescription theme={theme}>
								{t.services.adSpaces.description}
							</ServiceDescription>
							<ServiceButton theme={theme}>
								{t.services.adSpaces.viewDetails}
							</ServiceButton>
						</ServiceCard>
					</ServicesGrid>

					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</Container>
			</PageContainer>
		</React.Fragment>
	);
};

export default Services;
